import { z } from 'zod';

/** Engines and tools offered on the apply form (PRD 6.1). The last one is the catch-all. */
export const ENGINES = [
  'Phaser',
  'PixiJS',
  'Three.js',
  'Babylon.js',
  'PlayCanvas',
  'Unity (WebGL)',
  'Godot (Web)',
  'Construct',
  'GameMaker',
  'Defold',
  'My own engine or something else',
] as const;

/** ISO 3166-1 alpha-2 codes; the web app shows them as names with `Intl.DisplayNames`. */
export const COUNTRY_CODES = [
  'AF',
  'AL',
  'DZ',
  'AD',
  'AO',
  'AG',
  'AR',
  'AM',
  'AU',
  'AT',
  'AZ',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'BE',
  'BZ',
  'BJ',
  'BT',
  'BO',
  'BA',
  'BW',
  'BR',
  'BN',
  'BG',
  'BF',
  'BI',
  'CV',
  'KH',
  'CM',
  'CA',
  'CF',
  'TD',
  'CL',
  'CN',
  'CO',
  'KM',
  'CG',
  'CD',
  'CR',
  'CI',
  'HR',
  'CU',
  'CY',
  'CZ',
  'DK',
  'DJ',
  'DM',
  'DO',
  'EC',
  'EG',
  'SV',
  'GQ',
  'ER',
  'EE',
  'SZ',
  'ET',
  'FJ',
  'FI',
  'FR',
  'GA',
  'GM',
  'GE',
  'DE',
  'GH',
  'GR',
  'GD',
  'GT',
  'GN',
  'GW',
  'GY',
  'HT',
  'HN',
  'HK',
  'HU',
  'IS',
  'IN',
  'ID',
  'IR',
  'IQ',
  'IE',
  'IL',
  'IT',
  'JM',
  'JP',
  'JO',
  'KZ',
  'KE',
  'KI',
  'KP',
  'KR',
  'XK',
  'KW',
  'KG',
  'LA',
  'LV',
  'LB',
  'LS',
  'LR',
  'LY',
  'LI',
  'LT',
  'LU',
  'MO',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MT',
  'MH',
  'MR',
  'MU',
  'MX',
  'FM',
  'MD',
  'MC',
  'MN',
  'ME',
  'MA',
  'MZ',
  'MM',
  'NA',
  'NR',
  'NP',
  'NL',
  'NZ',
  'NI',
  'NE',
  'NG',
  'MK',
  'NO',
  'OM',
  'PK',
  'PW',
  'PS',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PL',
  'PT',
  'PR',
  'QA',
  'RO',
  'RU',
  'RW',
  'KN',
  'LC',
  'VC',
  'WS',
  'SM',
  'ST',
  'SA',
  'SN',
  'RS',
  'SC',
  'SL',
  'SG',
  'SK',
  'SI',
  'SB',
  'SO',
  'ZA',
  'SS',
  'ES',
  'LK',
  'SD',
  'SR',
  'SE',
  'CH',
  'SY',
  'TW',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TO',
  'TT',
  'TN',
  'TR',
  'TM',
  'TV',
  'UG',
  'UA',
  'AE',
  'GB',
  'US',
  'UY',
  'UZ',
  'VU',
  'VA',
  'VE',
  'VN',
  'YE',
  'ZM',
  'ZW',
] as const;

export const APPLICATION_STATUSES = [
  /** Sent, email not confirmed yet; never shown in the review queue. */
  'unconfirmed',
  'pending',
  'awaiting_applicant',
  'approved',
  'rejected',
] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export const REJECTION_REASONS = [
  'No playable game at the links',
  'Not a browser game',
  'Application incomplete',
  'Content we don’t host',
  'Other',
] as const;
export type RejectionReason = (typeof REJECTION_REASONS)[number];

/** What the applicant reads for each reason; "Other" relies on the reviewer's own message. */
export const REJECTION_REASON_TEXT: Record<RejectionReason, string> = {
  'No playable game at the links':
    'We couldn’t find a playable game at the links you shared. Send us a link to a finished browser game and we’ll gladly look again.',
  'Not a browser game':
    'MOGZO hosts games that run in a web browser, and the games you shared don’t run in one yet.',
  'Application incomplete': 'Your application was missing details we need to review it.',
  'Content we don’t host':
    'The games you shared include content MOGZO doesn’t host. Our Developer Terms list what we can’t accept.',
  Other: '',
};

export const PLAN_MIN = 50;
export const PLAN_MAX = 1000;
export const ANSWER_MAX = 2000;
export const QUESTION_MAX = 2000;
export const MAX_LINKS = 3;
/** Days a rejected applicant waits before applying again. */
export const REAPPLY_AFTER_DAYS = 30;
/** Days an applicant has to answer a question. */
export const ANSWER_WINDOW_DAYS = 14;
/** Hours the approval email's set-password link works. */
export const SETUP_LINK_HOURS = 48;
/** Bump when the Developer Terms change; stored with each application. */
export const DEVELOPER_TERMS_VERSION = '2026-10-24';

const webLink = z
  .url({ protocol: /^https?$/, message: 'Use a full web address starting with https://' })
  .max(300);

/** What the public apply form sends. */
export const developerApplicationSchema = z.object({
  name: z.string().trim().min(1, 'Enter your name').max(80),
  email: z.email('Enter a valid email address').max(254),
  studio: z.string().trim().max(80).optional(),
  country: z.enum(COUNTRY_CODES, 'Choose a country'),
  engine: z.enum(ENGINES, 'Choose an engine or tool'),
  links: z
    .array(webLink)
    .min(1, 'Add at least one link')
    .max(MAX_LINKS, `Add at most ${MAX_LINKS} links`),
  plan: z
    .string()
    .trim()
    .min(PLAN_MIN, `Tell us a bit more (at least ${PLAN_MIN} characters)`)
    .max(PLAN_MAX),
  acceptTerms: z.literal(true, 'Agree to the Developer Terms to apply'),
});
export type DeveloperApplicationInput = z.infer<typeof developerApplicationSchema>;
